import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeHideableValue} from '../../client-server-common/types/doppe-hideable-value';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisible
} from '../../client-server-common/types/doppe-hideable-value';
import type {OmitStrict, PartiallyOptional, PartiallyRequired} from '@wix/devzai-utils-common';
import {immerProduce} from '@wix/devzai-utils-common';
import type {WixImageResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {RichTextContent} from '@wix/devzai-utils-react';
import type {
    DoppeDtoContactMethod,
    DoppeDtoContactMethodDeprecated
} from '../../client-server-common/types/doppe-dto-contact-method';
import type {DoppeActionFullName} from '../../client-server-common/types/doppe-action-full-name';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeActionCTAType
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {BymoPagePropsContentSettingsMetadata} from "../../client-server-common";
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata
} from "../../client-server-common";

export namespace DoppeActionTypeContactCard {

    export interface ContactSettings {
        media: WixImageResource[] | null;
        description: DoppeHideableValue<RichTextContent>;
        name: DoppeHideableValue<DoppeActionFullName>;
        jobTitle: DoppeHideableValue<string>;
        contactMethods: DoppeDtoContactMethod[];
        company: DoppeHideableValue<string>;
        notes: string;
        saveContactProfileImage: DoppeHideableValue<WixImageResource | null>
    }

    export interface ActionSettings extends
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        ContactSettings
    {

    }


    export type PartialActionSettings = Partial<OmitStrict<DoppeActionTypeContactCard.ActionSettings, 'contactMethods'>>
        & DeprecatedFields & {
    }

    export type xxx = DoppeDtoContactMethod & DoppeDtoContactMethodDeprecated;

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        contactMethods?: PartiallyRequired<Partial<xxx>, 'id'>[]
        buttonText?: DoppeHideableValue<string>;
    }
}

export const doppeActionTypeContactCardActionContentSettingsMetadata = {
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    jobTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.PersonJobTitle),
    company: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCompany),
    contactMethods: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.ContactMethods, BymoPagePropsContentSettingSemanticMeaning.EntityContactMethods),
    name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.PersonFullName, BymoPagePropsContentSettingSemanticMeaning.PersonName),
    notes: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCompany, {
        deprecated: true
    }),
    media: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleImages, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    saveContactProfileImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleImage, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeContactCard.ActionSettings>

export const doppeActionTypeContactCard = doppeActionTypeDefineMetadata<
    DoppeActionTypeContactCard.ActionSettings,
    DoppeActionTypeContactCard.PartialActionSettings
>({
    id: 'io.bymo.action/contact-card',
    resolveActionSettings: action => {

        const {
            buttonText = '',
            notes = '',
            ctaButton,
            contactMethods = [],
            ctaButtons,
            sections,
            saveContactProfileImage = doppeHideableValueCreateHidden(null),
            ...restSettings
        } = action.settings;

        return {
            contactMethods: contactMethods.map(method => {

                const {
                    hidden
                } = method

                return {
                    enabled: !hidden,
                    ...method as PartiallyOptional<DoppeActionTypeContactCard.xxx, 'enabled'>,
                }
            }) as DoppeDtoContactMethod[],
            buttonText: buttonText,
            media: null,
            name: doppeHideableValueCreateHidden({
                firstName: '',
                lastName: ''
            }),
            tagLine: doppeHideableValueCreateHidden(''),
            company: doppeHideableValueCreateHidden(''),
            jobTitle: doppeHideableValueCreateHidden(''),
            description: doppeHideableValueCreateHidden(''),
            notes: '',
            saveContactProfileImage: doppeHideableValueCreateHidden(null),
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: doppeHideableValueGetVisibleValue(buttonText, ''),
                showButton: doppeHideableValueIsVisible(buttonText),
                settings: {
                    type: DoppeActionCTAType.SaveContact,
                    notes:  notes,
                    saveContactProfileImage: saveContactProfileImage
                }
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: false,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...restSettings
        }
    },
    isWidgetOpener: true,
    isLinkOpener: false,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeContactCardActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.SaveContact]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        }
    },
    actionListItemSettingsMetadata: false,
    resolveUsedMedia: actionSettings => {
        return actionSettings.media ?? [];
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.media?.[0] ?? null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.media) {
                actionSettings.media = actionSettings.media.map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            htmlSiteInfo: htmlSiteInfo,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
});
